'use client';

import { Label } from '@heroui/react';
import { CellSwitch } from '@heroui-pro/react';
import { NumberStepper } from '@heroui-pro/react/number-stepper';
import {
  GRID_COLUMNS,
  GRID_GUTTER,
  GRID_MARGIN,
  GRID_ROWS,
} from '@/components/canvas/page/guideSettings';
import type { GridGuideSettings, PageGuideSettings } from '@/components/canvas/document/publicTypes';

type CanvasGuideSettingsSectionProps = {
  guideSettings: PageGuideSettings;
  onVisibleChange: (visible: boolean) => void;
  onGridChange: (patch: Partial<GridGuideSettings>) => void;
};

function GuideNumberRow({
  label,
  ariaLabel,
  value,
  spec,
  onChange,
}: {
  label: string;
  ariaLabel: string;
  value: number;
  spec: { min: number; max: number; step: number };
  onChange: (value: number) => void;
}) {
  return (
    <div className="grid w-full grid-cols-[1fr_auto] items-center gap-3 px-3 py-0.5">
      <Label>{label}</Label>
      <NumberStepper
        aria-label={ariaLabel}
        size="sm"
        className="justify-self-end"
        value={value}
        minValue={spec.min}
        maxValue={spec.max}
        step={spec.step}
        onChange={(v) => {
          if (v == null || Number.isNaN(v)) return;
          onChange(v);
        }}
      >
        <NumberStepper.Group>
          <NumberStepper.DecrementButton />
          <NumberStepper.Value />
          <NumberStepper.IncrementButton />
        </NumberStepper.Group>
      </NumberStepper>
    </div>
  );
}

/** 画布属性 — 栅格辅助线 */
export function CanvasGuideSettingsSection({
  guideSettings,
  onVisibleChange,
  onGridChange,
}: CanvasGuideSettingsSectionProps) {
  const { enabled: visible, grid } = guideSettings;

  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold text-default-foreground">辅助线</h3>
      <div className="space-y-2 p-1">
        <CellSwitch aria-label="显示辅助线" isSelected={visible} onChange={onVisibleChange}>
          <CellSwitch.Trigger>
            <CellSwitch.Label>{visible ? '显示' : '隐藏'}</CellSwitch.Label>
            <CellSwitch.Control />
          </CellSwitch.Trigger>
        </CellSwitch>

        {visible && (
          <div className="w-full space-y-2 pt-1">
            <GuideNumberRow
              label="列数"
              ariaLabel="栅格列数"
              value={grid.columns}
              spec={GRID_COLUMNS}
              onChange={(columns) => onGridChange({ columns })}
            />
            <GuideNumberRow
              label="行数"
              ariaLabel="栅格行数"
              value={grid.rows}
              spec={GRID_ROWS}
              onChange={(rows) => onGridChange({ rows })}
            />
            <GuideNumberRow
              label="间距"
              ariaLabel="栅格间距"
              value={grid.gutter}
              spec={GRID_GUTTER}
              onChange={(gutter) => onGridChange({ gutter })}
            />
            <GuideNumberRow
              label="边距"
              ariaLabel="栅格边距"
              value={grid.margin}
              spec={GRID_MARGIN}
              onChange={(margin) => onGridChange({ margin })}
            />
          </div>
        )}
      </div>
    </div>
  );
}
